import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { API } from '../api';
import CustomItemModal from '../components/CustomItemModal';
import { openLabels } from '../labels';

export default function Manifest() {
  // the custom item currently being edited: { order, item }
  const [blankEdit, setBlankEdit] = useState(null);
  // partial booking: orderId -> Set-like map of itemId -> true (items to ship)
  const [partialSel, setPartialSel] = useState({});
  const [partialBooking, setPartialBooking] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [priority, setPriority] = useState('COD'); // COD first by default
  const [specificIds, setSpecificIds] = useState([]);
  const [selected, setSelected] = useState([]);
  const [booking, setBooking] = useState(false);
  const [bookingResult, setBookingResult] = useState(null);
  // The label roll in the office printer. The queue stops at the roll's safe
  // limit and holds the rest until someone presses "New roll loaded".
  const [roll, setRoll] = useState(null);
  const [rollBusy, setRollBusy] = useState(false);
  const [printingLabels, setPrintingLabels] = useState(false);
  const [lastWaybills, setLastWaybills] = useState([]);
  const iframeRef = useRef(null);
  const [attachingId, setAttachingId] = useState(null);
  const [settingBlankId, setSettingBlankId] = useState(null);
  const [overridingId, setOverridingId] = useState(null);
  const [forcedIds, setForcedIds] = useState([]);
  // Orders ticked for express (air) shipping. Empty by default — every
  // parcel goes standard surface unless it is ticked here, one at a time.
  const [expressIds, setExpressIds] = useState([]);
  const [sortKey, setSortKey] = useState('order');
  const [sortDir, setSortDir] = useState('asc');
  const [expandedId, setExpandedId] = useState(null);
  const [extSel, setExtSel] = useState({});       // itemId -> bool
  const [extAwb, setExtAwb] = useState('');
  const [extCourier, setExtCourier] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [view, setView] = useState('manifest'); // 'manifest' | 'history'
  const [history, setHistory] = useState(null);
  const [downloadingBatch, setDownloadingBatch] = useState(null);
  // Blob url of the labels PDF when it could not be opened in a tab (popup
  // blocked). It is shown inline instead, so a booking never ends with the
  // labels nowhere to be seen.
  const [inlineLabelUrl, setInlineLabelUrl] = useState(null);

  const itemKey = (order, item, i) => item._id || `${order._id}-${i}`;

  const fetchHistory = async () => {
    try {
      const r = (await axios.get(`${API}/api/manifest/history?limit=500`)).data;
      setHistory(Array.isArray(r?.orders) ? r : { orders: [] });
    } catch (e) { console.error(e); setHistory({ orders: [] }); }
  };

  // Group manifested orders into "batches": entries within 15 minutes
  // of each other belong to the same booking run
  const historyBatches = React.useMemo(() => {
    if (!history || !Array.isArray(history.orders)) return [];
    const sorted = [...history.orders].sort((a, b) => new Date(b.manifestedAt) - new Date(a.manifestedAt));
    const batches = [];
    for (const o of sorted) {
      const last = batches[batches.length - 1];
      if (last && Math.abs(new Date(last.orders[last.orders.length - 1].manifestedAt) - new Date(o.manifestedAt)) <= 15 * 60 * 1000) {
        last.orders.push(o);
      } else {
        batches.push({ orders: [o] });
      }
    }
    return batches;
  }, [history]);

  const handleDownloadBatch = async (batch, idx) => {
    setDownloadingBatch(idx);
    try {
      // This is the "Download Labels" button, so it opens the PDF rather than
      // printing. It used to call handlePrintLabels, which tries the local
      // agent first and only falls back to opening - so on the PC with the
      // printer attached, pressing Download silently printed instead.
      await handleDownloadLabels(batch.orders.map(o => o.waybill));
    } finally {
      setDownloadingBatch(null);
    }
  };

  // Open the label PDF without touching the printer. Manifest History is a
  // "give me that batch again" tool - it should never start a print run.
  const handleDownloadLabels = async (waybills) => {
    setPrintingLabels(true);
    try {
      const { skipped, popupBlocked } = await openLabels(waybills);
      if (skipped.length) {
        alert(`⚠ ${skipped.length} label(s) not ready yet and left out — retry shortly:
${skipped.join(', ')}`);
      }
      if (popupBlocked) alert('The label opened in a blocked popup — allow popups for this site.');
    } catch (e) {
      console.error('Label download failed:', e);
      alert('Labels could not be opened: ' + e.message);
    } finally {
      setPrintingLabels(false);
    }
  };

  const handleAssignExternal = async (order) => {
    const chosen = order.items.filter((it, i) => extSel[itemKey(order, it, i)] && it._id);
    if (chosen.length === 0) { alert('Tick at least one item to assign this AWB to.'); return; }
    if (!extAwb.trim()) { alert('Enter the AWB / tracking number.'); return; }
    setAssigning(true);
    try {
      const res = await axios.post(`${API}/api/manifest/assign-external-awb/${order._id}`, {
        itemIds: chosen.map(it => it._id),
        waybill: extAwb.trim(),
        courier: extCourier.trim()
      });
      alert(`✓ AWB ${res.data.waybill} assigned to ${res.data.itemsMarked.length} item(s).\n${res.data.shopifyNote}${res.data.orderFullyManifested ? '\nOrder fully manifested — leaving the list.' : ''}`);
      setExtSel({}); setExtAwb(''); setExtCourier(''); setExpandedId(null);
      await fetchEligible();
    } catch (e) {
      alert('Assign failed: ' + (e.response?.data?.message || e.message));
    } finally {
      setAssigning(false);
    }
  };

  const orderNum = (o) => parseInt(String(o.shopifyOrderNumber || '').replace(/\D/g, ''), 10) || 0;
  // What THIS parcel collects. A parcel of only replacements was paid for
  // with the original order, so the server books it prepaid even on a COD order.
  const replacementOnly = (o) => {
    const left = (o.items || []).filter(i => i.itemStatus !== 'manifested' && i.fulfilmentType !== 'external_pod');
    return left.length > 0 && left.every(i => i.isReplacement);
  };
  const parcelPay = (o) => (replacementOnly(o) ? 'Prepaid' : o.paymentMode);
  const payRank = (o) => (parcelPay(o) === 'COD' ? 0 : parcelPay(o) === 'Prepaid' ? 1 : 2);
  const sortedOrders = [...orders].sort((a, b) => {
    if (sortKey === 'payment') {
      const diff = sortDir === 'asc' ? payRank(a) - payRank(b) : payRank(b) - payRank(a);
      if (diff !== 0) return diff;
      return orderNum(a) - orderNum(b); // stable tiebreak within a payment group
    }
    return sortDir === 'asc' ? orderNum(a) - orderNum(b) : orderNum(b) - orderNum(a);
  });

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc'); // payment asc = COD first
    }
  };

  useEffect(() => {
    fetchEligible();
  }, [priority]);

  const fetchRoll = async () => {
    try {
      const res = await axios.get(`${API}/api/manifest/print-queue/roll`);
      setRoll(res.data);
    } catch (e) { /* the bar just stays hidden */ }
  };
  // Checked on load, after every booking, and every 30s while the page is
  // open, so a roll that fills up from another device shows here too.
  useEffect(() => {
    fetchRoll();
    const t = setInterval(fetchRoll, 30000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => { if (bookingResult) fetchRoll(); }, [bookingResult]);

  const newRollLoaded = async () => {
    if (!window.confirm('Is a NEW label roll in the printer now? The count starts again from 0 and any waiting labels will print.')) return;
    setRollBusy(true);
    try {
      const res = await axios.post(`${API}/api/manifest/print-queue/roll/new`, {});
      setRoll(res.data);
    } catch (e) {
      alert('Could not reset the roll: ' + (e.response?.data?.message || e.message));
    } finally {
      setRollBusy(false);
    }
  };

  const fetchEligible = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/manifest/eligible?priority=${priority}`);
      setOrders(res.data.orders);
      // only orders with stock allocated are selectable
      setSelected(res.data.orders.filter(o => o.manifestable).map(o => o._id));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (id) => {
    setSelected(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleBulkBook = async () => {
    if (selected.length === 0) return;
    setBooking(true);
    setBookingResult(null);
    try {
      // forceStock only bites on orders whose allocation fails, so sending it
      // for a mixed batch leaves the in-stock ones on the normal path
      const res = await axios.post(`${API}/api/manifest/bulk-book`, {
        priority,
        orderIds: selected,
        forceStock: selected.some(id => forcedIds.includes(id)),
        expressOrderIds: selected.filter(id => expressIds.includes(id))
      });
      setBookingResult(res.data);
      setForcedIds(prev => prev.filter(id => !selected.includes(id))); // consumed
      setExpressIds(prev => prev.filter(id => !selected.includes(id))); // consumed
      if (res.data.waybills) setLastWaybills(res.data.waybills);
      await fetchEligible();

      // Booking always OPENS the label PDF. Whether it also raises Chrome's
      // print dialog depends on who is putting the labels on paper:
      //
      //  - server queue OFF (the default): this device prints them, so the
      //    dialog comes up. Chrome cannot print without one, which suits the
      //    history here - auto-print was removed in July 2026 after a
      //    mis-click put a batch on paper unasked, and a dialog you dismiss
      //    costs nothing. Note it prints from whatever device booked, so a
      //    phone booking prints from the phone.
      //
      //  - server queue ON: the office agent is already printing these on the
      //    thermal printer. Raising a dialog here as well would mean a prompt
      //    on the phone AND paper in the office for the same labels.
      //
      // The booking response says which happened, so this reads the outcome
      // rather than guessing from a separate settings lookup - if the queue
      // declined the job (switched off, or every waybill already printed),
      // this device is the one that has to print.
      if (res.data.waybills && res.data.waybills.length) {
        await showLabels(res.data.waybills, { print: !res.data.printQueue?.queued });
      }
    } catch (e) {
      setBookingResult({ error: e.response?.data?.message || e.message });
    } finally {
      setBooking(false);
    }
  };

  // Stage a BLOCKED (out-of-stock) order for the next bulk booking instead of
  // booking it on the spot: ticking it enables its checkbox and flags it so
  // "Book" sends forceStock. Stock is still deducted then, so
  // counts may go negative — deliberate, it flags the miscount.
  const toggleForce = (order) => {
    const id = order._id;
    if (forcedIds.includes(id)) {
      setForcedIds(prev => prev.filter(x => x !== id));
      setSelected(prev => prev.filter(x => x !== id));
      return;
    }
    const shortLines = (order.missingDetails || []).flatMap(md =>
      (md.needs || []).map(n => `• ${n.type.toUpperCase()} ${n.label}${n.need != null ? ` — need ${n.need}, have ${n.have}` : ''}`)
    );
    if (!window.confirm(
      `Include ${order.shopifyOrderNumber} in the next booking despite being short?\n\n${shortLines.join('\n')}\n\n` +
      `It will be ticked and booked when you press "Book". Stock is still deducted, so these counts may go negative — that flags a miscount to reconcile. Only do this if the items are physically in hand.`
    )) return;
    setForcedIds(prev => [...prev, id]);
    setSelected(prev => (prev.includes(id) ? prev : [...prev, id]));
  };

  const handleOverrideHold = async (order) => {
    if (!window.confirm(
      `Manifest ${order.shopifyOrderNumber} now instead of waiting for the returning piece?\n\n` +
      `A matching return is in transit (${order.heldForItems.join(', ')}). Overriding will use fresh blank + print stock instead.`
    )) return;
    setOverridingId(order._id);
    try {
      await axios.post(`${API}/api/manifest/override-return-hold/${order._id}`, { override: true });
      await fetchEligible();
    } catch (e) {
      alert('Failed: ' + (e.response?.data?.message || e.message));
    } finally {
      setOverridingId(null);
    }
  };

  const handleAttachWaybill = async (order) => {
    const waybill = window.prompt(
      `Order ${order.shopifyOrderNumber} was already shipped?\n\nEnter its waybill (tracking/AWB) number:`
    );
    if (!waybill || !waybill.trim()) return;

    const courier = window.prompt(
      `Which courier shipped it?\n\ne.g. Delhivery, Bluedart, Xpressbees, Ekart...`,
      'Delhivery'
    );
    if (courier === null) return;

    setAttachingId(order._id);
    try {
      const res = await axios.post(`${API}/api/manifest/attach-waybill/${order._id}`, {
        waybill: waybill.trim(),
        courier: (courier || 'Delhivery').trim()
      });
      alert(`✓ ${res.data.courier} waybill attached to order ${order.shopifyOrderNumber}.\n${res.data.shopifyNote}\n(${res.data.stockNote})`);
      await fetchEligible();
    } catch (e) {
      alert(`Failed to attach waybill: ${e.response?.data?.message || e.message}`);
    } finally {
      setAttachingId(null);
    }
  };

  // Typed-in values are what got order 7511 stuck ("off white" never matched
  // the "Off White" on the shelf), so this is a picker now, not two prompts.
  const handleSetBlank = (order, item) => setBlankEdit({ order, item });

  // Ship only the ticked items; the rest stay pending on this order.
  const togglePartial = (orderId, itemId) => {
    setPartialSel(prev => {
      const forOrder = { ...(prev[orderId] || {}) };
      if (forOrder[itemId]) delete forOrder[itemId]; else forOrder[itemId] = true;
      return { ...prev, [orderId]: forOrder };
    });
  };

  const handleBookPartial = async (order) => {
    const itemIds = Object.keys(partialSel[order._id] || {});
    if (itemIds.length === 0) return;

    const shipping = order.items.filter(i => itemIds.includes(i._id));
    const holding = order.items.filter(i =>
      !itemIds.includes(i._id) && i.fulfilmentType !== 'external_pod' && i.itemStatus !== 'manifested'
    );
    const ok = window.confirm(
      `Ship now:\n  ${shipping.map(i => `${i.designName} ${i.color} ${i.size}${i.quantity > 1 ? ` × ${i.quantity}` : ''}`).join('\n  ')}\n\n` +
      `Hold back (stays on this tab, no stock used):\n  ${holding.map(i => `${i.designName} ${i.color} ${i.size}${i.quantity > 1 ? ` × ${i.quantity}` : ''}`).join('\n  ') || '(nothing)'}\n\n` +
      `The customer is notified about the shipped items only. Continue?`
    );
    if (!ok) return;

    setPartialBooking(order._id);
    const book = (force) =>
      axios.post(`${API}/api/manifest/book-partial/${order._id}`, {
        itemIds, forceStock: force, express: expressIds.includes(order._id)
      });

    try {
      let res;
      try {
        res = await book(forcedIds.includes(order._id));
      } catch (err) {
        // Short of stock is a question, not a dead end — you may be holding
        // the piece the count doesn't know about. Forcing records the real
        // consumption by letting the count go negative, which is the agreed
        // miscount signal rather than something quietly swallowed.
        const msg = err.response?.data?.message || err.message;
        if (!/out of stock/i.test(msg)) throw err;
        const anyway = window.confirm(
          `${msg}\n\nShip anyway?\n\nThe shortfall gets recorded by letting that count go negative, ` +
          `so it shows up as a miscount to fix rather than being hidden.`
        );
        if (!anyway) { setPartialBooking(null); return; }
        res = await book(true);
      }
      alert(
        `✓ ${res.data.message}\nAWB ${res.data.waybill}\n` +
        `Shipped: ${res.data.shipped.join(', ')}\n` +
        `Held back: ${res.data.heldBack.join(', ') || '(nothing)'}\n${res.data.shopifyNote}`
      );
      if (res.data.waybill) setLastWaybills([res.data.waybill]);
      // Surface the same banner bulk booking uses, so the "Print Labels"
      // button is there when you are ready to print.
      setBookingResult({ booked: 1, failed: 0, partial: true });
      setPartialSel(prev => ({ ...prev, [order._id]: {} }));
      await fetchEligible();

      // As with a bulk booking, nothing prints automatically - the banner's
      // "Print Labels" button does it when you are ready.
    } catch (e) {
      alert('Partial booking failed: ' + (e.response?.data?.message || e.message));
    } finally {
      setPartialBooking(null);
    }
  };

  // Put the labels on screen without touching any printer. Used automatically
  // after a booking and by the banner's "Open PDF" button.
  //
  // A new tab is the nicer result, but the browser blocks window.open when it
  // happens this long after the click that started the booking. Rather than
  // leave the labels unreachable, a blocked popup falls back to showing the
  // same PDF inline under the banner.
  const showLabels = async (waybills, { print = false } = {}) => {
    setPrintingLabels(true);
    try {
      const { skipped, popupBlocked, url } = await openLabels(waybills, { print });
      if (popupBlocked) {
        setInlineLabelUrl(url);
      } else {
        setInlineLabelUrl(null);
      }
      if (skipped.length) {
        alert(`⚠ ${skipped.length} label(s) not ready yet and left out — retry shortly:\n${skipped.join(', ')}`);
      }
      return { popupBlocked };
    } catch (e) {
      console.error('Opening labels failed:', e);
      alert('Labels could not be opened: ' + e.message);
      return { popupBlocked: false };
    } finally {
      setPrintingLabels(false);
    }
  };

  // Print through Chrome's own PDF viewer: opens the tab, then raises Chrome's
  // print dialog on it. Unlike the SumatraPDF agent this is NOT silent - the
  // printer is chosen in the dialog - but it works on any machine, with no
  // local agent and no SumatraPDF install.
  const handlePrintViaChrome = async (waybills) => {
    const { popupBlocked } = await showLabels(waybills, { print: true });
    if (popupBlocked) {
      // Nothing to raise a dialog on; print the inline copy instead.
      setTimeout(() => {
        try { iframeRef.current?.contentWindow?.print(); } catch (e) { /* Ctrl+P still works */ }
      }, 1200);
    }
  };

  // There is deliberately no "print on the thermal printer" button any more.
  // It pushed to an agent on 127.0.0.1, so it only ever did anything when the
  // dashboard was open on the office PC itself - useless from a phone. The
  // server now queues the job on booking and the office agent collects it.

  const manifestableOrders = orders.filter(o => o.manifestable);
  const heldCount = orders.filter(o => o.onHoldForReturn).length;
  // "blocked" = has in-house items to ship but stock can't cover them
  // (held-for-return orders are counted separately, not as blocked)
  const waitsForQc = (o) => (o.waitingForReturnQc || []).length > 0;
  const blockedCount = orders.filter(o => o.hasInHouseToShip && !o.manifestable && !o.onHoldForReturn && !waitsForQc(o)).length;
  const cod = manifestableOrders.filter(o => parcelPay(o) === 'COD').length;
  const prepaid = manifestableOrders.filter(o => parcelPay(o) === 'Prepaid').length;

  return (
    <div>
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
        {[['manifest', 'Manifest'], ['history', 'Manifest History']].map(([v, label]) => (
          <button key={v} onClick={() => { setView(v); if (v === 'history') fetchHistory(); }} style={{
            padding: '9px 18px', fontSize: '14px', borderRadius: '8px', cursor: 'pointer',
            background: view === v ? '#6e1f2a' : '#ffffff', color: view === v ? '#fff' : '#7d6a63',
            border: `1px solid ${view === v ? '#6e1f2a' : '#e6dccc'}`
          }}>{label}</button>
        ))}
      </div>

      {roll && (() => {
        const stopped = roll.full && roll.waiting > 0;
        const low = !stopped && (roll.willPause || roll.remaining <= 30);
        const bg = stopped ? '#fdecea' : low ? '#fff6e0' : '#f6f2ea';
        const fg = stopped ? '#b3261e' : low ? '#8a5a00' : '#7d6a63';
        return (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap',
            background: bg, color: fg, border: `1px solid ${stopped ? '#f3b8b2' : '#e6dccc'}`,
            borderRadius: '10px', padding: '10px 14px', marginBottom: '16px', fontSize: '13px'
          }}>
            <div>
              {stopped ? (
                <><b>🏷 Label roll used up — printing has stopped.</b> {roll.waiting} label(s) waiting. Put in a new roll, then press the button.</>
              ) : (
                <>🏷 Label roll: <b>{roll.remaining}</b> left of {roll.capacity}
                  {roll.waiting > 0 ? ` · ${roll.waiting} waiting to print` : ''}
                  {roll.willPause ? ' · the roll will run out partway — keep a new roll ready' : low ? ' · running low, keep a new roll ready' : ''}</>
              )}
            </div>
            <button onClick={newRollLoaded} disabled={rollBusy} style={{
              padding: '6px 12px', fontSize: '12px', borderRadius: '8px', cursor: 'pointer',
              background: stopped ? '#6e1f2a' : '#ffffff', color: stopped ? '#fff' : '#6e1f2a',
              border: '1px solid #6e1f2a'
            }}>{rollBusy ? 'Saving…' : 'New roll loaded'}</button>
          </div>
        );
      })()}

      {view === 'history' && (
        <div className="card">
          <div className="card-title">Manifest History — download labels of any past batch</div>
          {!history ? <p style={{ color: '#93827a' }}>Loading...</p> : historyBatches.length === 0 ? (
            <p style={{ color: '#93827a' }}>No manifested orders yet</p>
          ) : historyBatches.map((batch, idx) => {
            const first = batch.orders[batch.orders.length - 1];
            const dt = new Date(first.manifestedAt);
            const couriers = {};
            batch.orders.forEach(o => { couriers[o.courier] = (couriers[o.courier] || 0) + 1; });
            return (
              <div key={idx} style={{ border: '1px solid #e6dccc', borderRadius: '10px', padding: '14px 16px', marginBottom: '14px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                  <div>
                    <div style={{ fontWeight: 600, color: '#2e1f23' }}>
                      {dt.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} · {dt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                      <span style={{ color: '#8c3242', marginLeft: '10px' }}>{batch.orders.length} order{batch.orders.length > 1 ? 's' : ''}</span>
                    </div>
                    <div style={{ fontSize: '12px', color: '#7d6a63', marginTop: '4px' }}>
                      {Object.entries(couriers).map(([c, n]) => `${c}: ${n}`).join(' · ')}
                      {batch.orders.some(o => o.partial) && (
                        <span style={{ color: '#7a6a12', marginLeft: '8px' }}>
                          · {batch.orders.filter(o => o.partial).length} part shipped (POD item still open)
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    className="btn-primary"
                    disabled={downloadingBatch === idx || printingLabels}
                    onClick={() => handleDownloadBatch(batch, idx)}
                    style={{ fontSize: '13px', padding: '8px 14px' }}
                  >
                    {downloadingBatch === idx ? 'Preparing...' : `⬇ Download Labels (${batch.orders.length})`}
                  </button>
                </div>
                <details style={{ marginTop: '10px' }}>
                  <summary style={{ cursor: 'pointer', color: '#8c3242', fontSize: '13px' }}>Show orders</summary>
                  <div style={{ marginTop: '8px', fontSize: '13px', color: '#4a3840' }}>
                    {batch.orders.map(o => (
                      <div key={o.waybill} style={{ padding: '3px 0', borderBottom: '1px solid #e6dccc' }}>
                        <span style={{ fontFamily: 'monospace', color: '#8c3242' }}>{o.orderNumber}</span>
                        {' '}· {o.customerName} · {o.courier} <span style={{ fontFamily: 'monospace' }}>{o.waybill}</span>
                        {o.partial && (
                          <span
                            style={{ marginLeft: '8px', fontSize: '11px', color: '#7a6a12' }}
                            title="This parcel shipped; the order also has POD item(s) still waiting for their own AWB"
                          >
                            part shipped{o.pendingItems ? ` — ${o.pendingItems} POD item(s) left` : ''}
                          </span>
                        )}
                        {o.trackingStatus && <span style={{ marginLeft: '8px', fontSize: '11px', color: '#2e7d50' }}>{o.trackingStatus.replace('_', ' ')}</span>}
                      </div>
                    ))}
                  </div>
                </details>
              </div>
            );
          })}
        </div>
      )}

      {view === 'manifest' && (<>
      <div className="stat-grid">
        <div className="stat-card">
          <div className="stat-label">Ready to Manifest</div>
          <div className="stat-value">
            {manifestableOrders.length}
            {blockedCount > 0 && (
              <span style={{ fontSize: '13px', color: '#b3261e', marginLeft: '8px' }}>
                +{blockedCount} blocked
              </span>
            )}
            {heldCount > 0 && (
              <span style={{ fontSize: '13px', color: '#7a6a12', marginLeft: '8px' }}>
                +{heldCount} held ⏳
              </span>
            )}
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label">COD Orders</div>
          <div className="stat-value" style={{ color: '#7a6a12' }}>{cod}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Prepaid Orders</div>
          <div className="stat-value" style={{ color: '#2e7d50' }}>{prepaid}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Selected</div>
          <div className="stat-value" style={{ color: '#8c3242' }}>{selected.length}</div>
        </div>
      </div>

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
          <div className="card-title" style={{ margin: 0 }}>Orders Ready to Manifest</div>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
            <select
              value={priority}
              onChange={e => setPriority(e.target.value)}
              style={{ minWidth: '180px' }}
            >
              <option value="default">Default — No Priority</option>
              <option value="COD">Prioritize COD</option>
            </select>
            <button
              onClick={() => setSelected(manifestableOrders.map(o => o._id))}
              style={{
                padding: '10px 16px',
                fontSize: '13px',
                background: '#ffffff',
                color: '#8c3242',
                border: '1px solid #e6dccc',
                borderRadius: '8px',
                cursor: 'pointer'
              }}
              title="Select every order that has stock available right now"
            >
              ✓ Select All Manifestable
            </button>
            <button
              className="btn-primary"
              onClick={handleBulkBook}
              disabled={booking || selected.length === 0}
              style={{ padding: '10px 20px' }}
            >
              {booking ? 'Booking...' : `Book (${selected.length})`}
            </button>
          </div>
        </div>


        {bookingResult && (
          <div style={{
            background: bookingResult.error ? '#fbe9e7' : '#e8f3ec',
            border: `1px solid ${bookingResult.error ? '#b3261e' : '#2e7d50'}`,
            borderRadius: '8px',
            padding: '12px 16px',
            marginBottom: '20px',
            fontSize: '13px',
            color: bookingResult.error ? '#b3261e' : '#2e7d50'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>
                {bookingResult.error
                  ? `Error: ${bookingResult.error}`
                  : `✓ ${bookingResult.booked} orders booked successfully. ${bookingResult.failed > 0 ? `${bookingResult.failed} failed.` : ''}`
                }
              </span>
              {lastWaybills.length > 0 && (
                <span style={{ display: 'flex', gap: '8px', marginLeft: '16px' }}>
                  {[
                    // The PDF already opened by itself on booking; this is for
                    // getting it back after the tab was closed.
                    ['Open PDF', () => showLabels(lastWaybills)],
                    // Chrome's print dialog - works anywhere, asks which
                    // printer. The office thermal printer needs no button at
                    // all now: the server queues the job and the agent in the
                    // office collects it, wherever the booking came from.
                    ['Print (Chrome)', () => handlePrintViaChrome(lastWaybills)]
                  ].map(([label, onClick]) => (
                    <button
                      key={label}
                      onClick={onClick}
                      disabled={printingLabels}
                      style={{
                        fontSize: '12px',
                        padding: '6px 14px',
                        background: '#ffffff',
                        color: '#8c3242',
                        border: '1px solid #e6dccc',
                        borderRadius: '6px',
                        cursor: printingLabels ? 'default' : 'pointer',
                        opacity: printingLabels ? 0.6 : 1,
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </span>
              )}
            </div>
            {bookingResult.courierSummary && Object.keys(bookingResult.courierSummary).length > 0 && (
              <div style={{ marginTop: '10px', fontSize: '13px', color: '#4a3840' }}>
                {Object.entries(bookingResult.courierSummary).map(([courier, n]) => (
                  <span key={courier} style={{
                    display: 'inline-block', marginRight: '10px', padding: '3px 10px',
                    borderRadius: '6px', background: courier === 'Delhivery' ? '#ffffff' : '#f5f1d6',
                    border: `1px solid ${courier === 'Delhivery' ? '#e6dccc' : '#7a6a12'}`
                  }}>
                    {courier}: {n} order{n > 1 ? 's' : ''}
                  </span>
                ))}
                {bookingResult.shiprocketUsed && (
                  <span style={{ color: '#7a6a12', marginLeft: '4px' }}>
                    ⚡ Shiprocket used where Delhivery couldn't service the pincode
                  </span>
                )}
              </div>
            )}
            {bookingResult.results && bookingResult.results.some(r => r.status === 'failed') && (
              <ul style={{ marginTop: '10px', marginBottom: 0, paddingLeft: '18px', color: '#b3261e' }}>
                {bookingResult.results.filter(r => r.status === 'failed').map((r, i) => (
                  <li key={i}>{r.orderNumber}: {r.reason}</li>
                ))}
              </ul>
            )}
            {/* Only shown when the server queue actually took the job. It is
                switched off by default, so normally there is nothing to say
                here and the line stays out of the way. */}
            {bookingResult.printQueue && bookingResult.printQueue.queued && (
              <div style={{ marginTop: '8px', fontSize: '12px', color: '#2e7d50' }}>
                🖨 {bookingResult.printQueue.count} label(s) also sent to the office printer
                {bookingResult.printQueue.duplicates
                  ? ` · ${bookingResult.printQueue.duplicates} already printed earlier, not repeated`
                  : ''}
              </div>
            )}
            {inlineLabelUrl && (
              <div style={{ marginTop: '12px' }}>
                <div style={{ fontSize: '12px', color: '#7d6a63', marginBottom: '6px' }}>
                  The browser blocked the new tab, so the labels are shown here. Allow
                  popups for this site to get them in their own tab instead.
                </div>
                <iframe
                  ref={iframeRef}
                  src={inlineLabelUrl}
                  title="Shipping labels"
                  style={{ width: '100%', height: '520px', border: '1px solid #e6dccc', borderRadius: '8px', background: '#fff' }}
                />
              </div>
            )}
          </div>
        )}

        {loading ? <p style={{ color: '#93827a' }}>Loading...</p> : (
          <table>
            <thead>
              <tr>
                <th style={{ width: '40px' }}>
                  <input
                    type="checkbox"
                    checked={selected.length === manifestableOrders.length && manifestableOrders.length > 0}
                    onChange={e => setSelected(e.target.checked ? manifestableOrders.map(o => o._id) : [])}
                    title="Select/deselect all manifestable orders"
                  />
                </th>
                <th
                  onClick={() => handleSort('order')}
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  title="Click to sort by order number"
                >
                  Order # {sortKey === 'order' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                </th>
                <th>Customer</th>
                <th>Items</th>
                <th
                  onClick={() => handleSort('payment')}
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  title="Click to sort by payment mode (COD first)"
                >
                  Payment {sortKey === 'payment' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                </th>
                <th>Amount</th>
                <th>Address</th>
                <th style={{ width: '90px' }}></th>
              </tr>
            </thead>
            <tbody>
              {sortedOrders.map(order => {
                const externalPending = order.items.filter(i => i.fulfilmentType === 'external_pod' && i.itemStatus !== 'manifested');
                // pieces, not lines — a qty-2 line is ONE record but TWO garments
                const externalPendingUnits = externalPending.reduce((s, i) => s + (Number(i.quantity) || 1), 0);
                const isForced = forcedIds.includes(order._id);
                const isExpress = expressIds.includes(order._id);
                // a staged (forced) order is bookable even though stock is short
                const canBook = order.manifestable || isForced;
                const rowDim = canBook ? (selected.includes(order._id) ? 1 : 0.5) : 0.85;
                return (
                <React.Fragment key={order._id}>
                <tr style={{ opacity: rowDim, ...(isForced ? { outline: '1px solid #f3cfcb' } : {}) }}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selected.includes(order._id)}
                      disabled={!canBook}
                      onChange={() => toggleSelect(order._id)}
                      title={isForced
                        ? 'Staged to book despite the shortage — stock may go negative'
                        : canBook ? 'Include this order\'s in-house items in the booking' : 'No in-house items to book (or out of stock)'}
                    />
                  </td>
                  <td style={{ fontFamily: 'monospace', color: '#8c3242' }}>
                    {order.shopifyOrderNumber}
                    <div>
                      <button
                        onClick={() => setExpressIds(prev =>
                          prev.includes(order._id) ? prev.filter(x => x !== order._id) : [...prev, order._id]
                        )}
                        disabled={booking}
                        title={isExpress
                          ? 'Booking this one on the express (air) service — click for standard'
                          : 'Standard surface service. Click to book this order express (air) — costs more, only this order'}
                        style={{
                          marginTop: 3, fontSize: 10, padding: '1px 6px', lineHeight: 1.6,
                          fontFamily: 'inherit', cursor: 'pointer', borderRadius: 4,
                          background: isExpress ? '#f5f1d6' : 'transparent',
                          color: isExpress ? '#7a6a12' : '#b0a296',
                          border: `1px solid ${isExpress ? '#7a6a12' : '#e6dccc'}`
                        }}
                      >
                        {isExpress ? '⚡ Express' : 'Express'}
                      </button>
                    </div>
                    {externalPending.length > 0 && (
                      <div
                        onClick={() => setExpandedId(expandedId === order._id ? null : order._id)}
                        style={{ fontSize: '11px', color: '#7a6a12', cursor: 'pointer', marginTop: '2px' }}
                      >
                        {expandedId === order._id ? '▼' : '▶'} {externalPendingUnits} POD item{externalPendingUnits > 1 ? 's' : ''}
                      </div>
                    )}
                  </td>
                  <td>{order.customerName}</td>
                  <td style={{ fontSize: '13px' }}>
                    {order.items.map((item, i) => {
                      const manifested = item.itemStatus === 'manifested';
                      const ext = item.fulfilmentType === 'external_pod';
                      if (item.needsBlankReview) {
                        return (
                          <div key={i} style={{ marginBottom: '3px' }}>
                            <button
                              onClick={() => handleSetBlank(order, item)}
                              disabled={settingBlankId === item._id}
                              title="Custom item with no design behind it — click to choose which blank (colour + size) it will print on"
                              style={{
                                fontSize: '12px',
                                padding: '3px 8px',
                                background: '#f5f1d6',
                                color: '#7a6a12',
                                border: '1px solid #7a6a12',
                                borderRadius: '6px',
                                cursor: 'pointer'
                              }}
                            >
                              {settingBlankId === item._id ? 'Saving...' : `❓ ${item.designName} × ${item.quantity} — set blank`}
                            </button>
                          </div>
                        );
                      }
                      // POD items and already-shipped items are greyed —
                      // they can't be shipped by us from this booking
                      const dim = ext || manifested;
                      const shippable = !ext && !manifested;
                      const ticked = !!(partialSel[order._id] || {})[item._id];
                      return (
                        <div key={i} style={{
                          color: dim ? '#b0a296' : '#2e1f23',
                          textDecoration: manifested ? 'line-through' : 'none',
                          fontStyle: ext && !manifested ? 'italic' : 'normal'
                        }}>
                          {shippable && (
                            <input
                              type="checkbox"
                              checked={ticked}
                              onChange={() => togglePartial(order._id, item._id)}
                              title="Tick the items to ship now — anything left unticked stays pending on this order"
                              style={{ marginRight: 6, verticalAlign: 'middle' }}
                            />
                          )}
                          {item.designName} · {item.color} {item.size}
                          {item.designCode === 'CUSTOM' && !manifested && (
                            <button
                              onClick={() => handleSetBlank(order, item)}
                              title="Change the design or blank on this custom item"
                              style={{
                                marginLeft: 6, fontSize: 10, padding: '0 5px', cursor: 'pointer',
                                background: 'transparent', color: '#8c3242',
                                border: '1px solid #e6dccc', borderRadius: 4
                              }}
                            >
                              change
                            </button>
                          )}
                          {ext && <span style={{ marginLeft: '6px', fontSize: '10px', color: '#7a6a12', border: '1px solid #7a6a12', borderRadius: '4px', padding: '0 4px' }}>POD — not shipped by us</span>}
                          {manifested && <span style={{ marginLeft: '6px', fontSize: '10px', color: '#2e7d50' }}>✓ {item.courier}</span>}
                        </div>
                      );
                    })}
                    {(() => {
                      // Only worth offering when there is something to leave behind
                      const ticks = Object.keys(partialSel[order._id] || {});
                      const shippableCount = order.items.filter(
                        i => i.fulfilmentType !== 'external_pod' && i.itemStatus !== 'manifested'
                      ).length;
                      if (ticks.length === 0 || shippableCount < 2 || ticks.length === shippableCount) return null;
                      return (
                        <button
                          onClick={() => handleBookPartial(order)}
                          disabled={partialBooking === order._id}
                          title="Book a parcel containing only the ticked items. The rest stay on this tab and use no stock."
                          style={{
                            marginTop: 6, fontSize: 12, padding: '4px 10px', cursor: 'pointer',
                            background: '#e9eef3', color: '#2a6f8a',
                            border: '1px solid #2a6f8a', borderRadius: 6
                          }}
                        >
                          {partialBooking === order._id
                            ? 'Booking…'
                            : `📦 Ship ${ticks.length} of ${shippableCount} — hold the rest`}
                        </button>
                      );
                    })()}
                    {order.onHoldForReturn && (
                      <div style={{ color: '#7a6a12', fontSize: '11px', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <span>
                          ⏳ Held — waiting on a returning piece:
                          {(order.heldSources && order.heldSources.length > 0
                            ? order.heldSources
                            : (order.heldForItems || []).map(d => ({ desc: d }))
                          ).map((s, si) => (
                            <span key={si} style={{ display: 'block', marginTop: '2px' }}>
                              • {s.desc}
                              {s.fromOrder && (
                                <>
                                  {' '}from <span style={{ fontFamily: 'monospace', color: '#8c3242' }}>{s.fromOrder}</span>
                                  {s.kind ? ` (${s.kind})` : ''}
                                </>
                              )}
                              {s.waybill && (
                                <>
                                  {' · '}
                                  <a
                                    href={`https://www.delhivery.com/track/package/${s.waybill}`}
                                    target="_blank" rel="noreferrer"
                                    title={`Track ${s.waybill} (${s.courier || 'courier'})`}
                                    style={{ color: '#8c3242', fontFamily: 'monospace', textDecoration: 'underline' }}
                                  >
                                    {s.waybill}
                                  </a>
                                </>
                              )}
                              {s.etaDays != null && (
                                <span style={{ color: '#7a6a12' }}>
                                  {' · '}{s.etaDays <= 0.5 ? 'due any day' : `~${Math.round(s.etaDays)}d`}
                                </span>
                              )}
                            </span>
                          ))}
                          {order.heldEtaDate && (
                            <span style={{ color: '#7a6a12' }}>
                              est. ready by {new Date(order.heldEtaDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                            </span>
                          )}
                        </span>
                        <button
                          onClick={() => handleOverrideHold(order)}
                          disabled={overridingId === order._id}
                          title="Manifest now using fresh blank + print stock instead of waiting for the returning piece"
                          style={{
                            fontSize: '11px', padding: '3px 9px',
                            background: '#f5f1d6', color: '#7a6a12',
                            border: '1px solid #7a6a12', borderRadius: '6px', cursor: 'pointer', whiteSpace: 'nowrap'
                          }}
                        >
                          {overridingId === order._id ? '...' : 'Manifest anyway'}
                        </button>
                      </div>
                    )}
                    {waitsForQc(order) && (
                      <div style={{ color: '#7a6a12', fontSize: '11px', marginTop: '4px' }}>
                        🔁 Replacement — ships once the return is back and QC'd:
                        {order.waitingForReturnQc.map((w, wi) => (
                          <span key={wi} style={{ display: 'block', marginTop: '2px' }}>
                            • {w.desc} <span style={{ color: '#4a3840' }}>(return: {String(w.returnStatus).replace(/_/g, ' ')})</span>
                          </span>
                        ))}
                      </div>
                    )}
                    {/* keyed off the SERVER's view, not canBook — once staged,
                        canBook flips true and this block (with its undo
                        button) would vanish */}
                    {!order.manifestable && order.hasInHouseToShip && !order.onHoldForReturn && !waitsForQc(order) && (
                      <div style={{ fontSize: '11px', marginTop: '5px' }}>
                        {(order.missingDetails && order.missingDetails.length > 0) ? (
                          order.missingDetails.map((md, mi) => (
                            <div key={mi} style={{ marginBottom: '3px' }}>
                              <div style={{ color: '#b3261e', fontWeight: 600 }}>
                                ⛔ {md.item} · {md.colour} {md.size}{md.qty > 1 ? ` ×${md.qty}` : ''}
                              </div>
                              {(md.needs || []).map((nd, ni) => (
                                <div key={ni} style={{ marginLeft: '14px', color: '#4a3840', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  <span style={{
                                    fontSize: '9px', fontWeight: 700, letterSpacing: '0.5px',
                                    padding: '1px 5px', borderRadius: '4px', whiteSpace: 'nowrap',
                                    // blank vs print get distinct colours so it's obvious at a glance
                                    background: nd.type === 'blank' ? '#e9eef3' : nd.type === 'print' ? '#f2e9f6' : '#f5f1d6',
                                    color: nd.type === 'blank' ? '#2a6f8a' : nd.type === 'print' ? '#7b4b8a' : '#7a6a12',
                                    border: `1px solid ${nd.type === 'blank' ? '#2a6f8a' : nd.type === 'print' ? '#7b4b8a' : '#7a6a12'}`
                                  }}>
                                    {nd.type === 'blank' ? 'BLANK' : nd.type === 'print' ? 'PRINT' : 'ACTION'}
                                  </span>
                                  <span>{nd.label}</span>
                                  {nd.notTracked ? (
                                    <span style={{ color: '#b3261e' }}>not in prints inventory</span>
                                  ) : nd.need != null ? (
                                    <span style={{ color: '#b3261e', fontWeight: 600 }}>
                                      need {nd.need}, have {nd.have}
                                      <span style={{ color: '#b3261e', fontWeight: 400 }}> (short {nd.need - nd.have})</span>
                                    </span>
                                  ) : null}
                                </div>
                              ))}
                            </div>
                          ))
                        ) : (
                          <div style={{ color: '#b3261e' }}>
                            ⛔ Out of stock: {(order.missingItems && order.missingItems.length > 0)
                              ? order.missingItems.join('; ')
                              : 'stock unavailable'}
                          </div>
                        )}
                        <button
                          onClick={() => toggleForce(order)}
                          disabled={booking}
                          title={isForced
                            ? 'Staged for the next booking — click to remove'
                            : 'Tick this order for the next booking despite the shortage; stock may go negative, flagging a miscount'}
                          style={{
                            marginTop: '4px', marginLeft: '14px',
                            fontSize: '11px', padding: '3px 9px',
                            background: isForced ? '#e8f3ec' : '#fbe9e7',
                            color: isForced ? '#2e7d50' : '#b3261e',
                            border: `1px solid ${isForced ? '#2e7d50' : '#f3cfcb'}`,
                            borderRadius: '6px', cursor: 'pointer', whiteSpace: 'nowrap'
                          }}
                        >
                          {isForced ? '✓ Staged — will book (click to undo)' : '⚠ Manifest anyway'}
                        </button>
                      </div>
                    )}
                    {!order.hasInHouseToShip && externalPending.length > 0 && (
                      <div style={{ color: '#7a6a12', fontSize: '11px', marginTop: '4px' }}>
                        In-house part shipped — {externalPendingUnits} POD item(s) left; expand to add AWB
                      </div>
                    )}
                  </td>
                  <td>
                    {replacementOnly(order)
                      ? <span className="badge badge-green" title={`Replacement — already paid on the original ${order.paymentMode} order, collects ₹0`}>Prepaid · replacement</span>
                      : order.paymentMode === 'COD'
                      ? <span className="badge badge-yellow">COD</span>
                      : order.paymentMode === 'Prepaid'
                      ? <span className="badge badge-green">Prepaid</span>
                      : <span className="badge badge-blue">Unknown</span>
                    }
                  </td>
                  <td style={{ fontWeight: '600' }}>
                    ₹{order.orderTotal || order.orderValue || '—'}
                    {parcelPay(order) === 'COD' && order.codAmount > 0 && (
                      <div style={{ fontSize: '11px', color: '#7a6a12' }}>
                        COD: ₹{order.codAmount}
                      </div>
                    )}
                  </td>
                  <td style={{ fontSize: '12px', color: '#93827a' }}>
                    {order.customerAddress
                      ? `${order.customerAddress.city}, ${order.customerAddress.province} - ${order.customerAddress.zip}`
                      : '—'
                    }
                  </td>
                  <td>
                    <button
                      onClick={() => handleAttachWaybill(order)}
                      disabled={attachingId === order._id || booking}
                      title="Already shipped this order manually? Attach its waybill to remove it from this list."
                      style={{
                        fontSize: '11px',
                        padding: '4px 10px',
                        background: '#ffffff',
                        color: '#7d6a63',
                        border: '1px solid #e6dccc',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {attachingId === order._id ? 'Attaching...' : '🚚 Shipped?'}
                    </button>
                  </td>
                </tr>
                {expandedId === order._id && externalPending.length > 0 && (
                  <tr>
                    <td colSpan="8" style={{ background: '#f8f3eb', padding: '14px 20px' }}>
                      <div style={{ fontSize: '13px', color: '#2e1f23', marginBottom: '8px', fontWeight: 600 }}>
                        Other-POD items — tick the ones covered by one AWB, enter its tracking, and assign (tick several to club them):
                      </div>
                      {externalPending.map((item, i) => (
                        <label key={item._id} style={{ display: 'block', color: '#4a3840', fontSize: '13px', marginBottom: '5px', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={!!extSel[itemKey(order, item, i)]}
                            onChange={e => setExtSel(s => ({ ...s, [itemKey(order, item, i)]: e.target.checked }))}
                            style={{ marginRight: '8px' }}
                          />
                          {item.designName} · {item.color} {item.size}
                          {item.quantity > 1 && (
                            <span style={{
                              marginLeft: 6, fontSize: 11, fontWeight: 700, color: '#7a6a12',
                              border: '1px solid #7a6a12', borderRadius: 4, padding: '0 5px'
                            }}>
                              × {item.quantity} pieces — one AWB covers both
                            </span>
                          )}
                        </label>
                      ))}
                      <div style={{ display: 'flex', gap: '10px', marginTop: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                        <input
                          placeholder="AWB / tracking no."
                          value={expandedId === order._id ? extAwb : ''}
                          onChange={e => setExtAwb(e.target.value)}
                          style={{ padding: '6px 10px', width: '180px' }}
                        />
                        <input
                          placeholder="Courier (e.g. Bluedart)"
                          value={expandedId === order._id ? extCourier : ''}
                          onChange={e => setExtCourier(e.target.value)}
                          style={{ padding: '6px 10px', width: '170px' }}
                        />
                        <button
                          className="btn-primary"
                          onClick={() => handleAssignExternal(order)}
                          disabled={assigning}
                          style={{ fontSize: '13px', padding: '7px 14px' }}
                        >
                          {assigning ? 'Assigning...' : 'Assign AWB'}
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
                </React.Fragment>
                );
              })}
              {orders.length === 0 && (
                <tr>
                  <td colSpan="8" style={{ color: '#93827a', textAlign: 'center' }}>
                    No orders ready to manifest
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
      </>)}

      {blankEdit && (
        <CustomItemModal
          order={blankEdit.order}
          item={blankEdit.item}
          onClose={() => setBlankEdit(null)}
          onSaved={async (data) => {
            setBlankEdit(null);
            await fetchEligible();
            alert(`✓ ${data.item.designName} — ${data.item.color} ${data.item.size}`);
          }}
        />
      )}
    </div>
  );
}